import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table'
import type { Order } from '@/types'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { formatDate, formatCurrency } from '@/lib/utils'

interface OrdersColumnsProps {
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  allSelected?: boolean
}

const getStatusVariant = (
  status?: string
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' => {
  if (!status) return 'default'

  switch (status.toLowerCase()) {
    case 'approved':
    case 'processed':
      return 'success'
    case 'pending':
      return 'warning'
    case 'denied':
      return 'destructive'
    case 'draft':
      return 'secondary'
    default:
      return 'default'
  }
}

export const createOrdersColumns = ({
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
}: OrdersColumnsProps = {}): ColumnDef<Order>[] => [
  // Selection column only when selection is enabled (admins, for bulk owner update).
  ...(onToggleSelect
    ? [
        {
          id: 'select',
          header: () => (
            <Checkbox
              checked={allSelected}
              onCheckedChange={onToggleSelectAll}
              aria-label="Select all orders"
            />
          ),
          cell: ({ row }: { row: { original: Order } }) => (
            <Checkbox
              checked={selectedIds?.has(row.original.id) ?? false}
              onCheckedChange={() => onToggleSelect?.(row.original.id)}
              aria-label={`Select order ${row.original.order_number}`}
            />
          ),
          size: 50,
          minSize: 50,
          maxSize: 50,
          enableResizing: false,
        } as ColumnDef<Order>,
      ]
    : []),
  {
    id: 'order_number',
    header: 'Order Number',
    accessorKey: 'order_number',
    meta: { sortField: 'order_number' },
    size: 180,
    cell: ({ row }) => (
      <Link
        to={`/orders/${row.original.id}`}
        className="font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label={`View details for order ${row.original.order_number}`}
      >
        {row.original.order_number || '-'}
      </Link>
    ),
  },
  {
    id: 'account_name',
    header: 'Account',
    accessorKey: 'account_name',
    meta: { sortField: 'account_name' },
    size: 200,
    cell: ({ row }) =>
      row.original.account_id && row.original.account_name ? (
        <Link
          to={`/accounts/${row.original.account_id}`}
          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label={`View account: ${row.original.account_name}`}
        >
          {row.original.account_name}
        </Link>
      ) : (
        <span>{row.original.account_name || '-'}</span>
      ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    meta: { sortField: 'status' },
    size: 130,
    cell: ({ row }) => (
      <Badge variant={getStatusVariant(row.original.status)}>
        {row.original.status || 'Unknown'}
      </Badge>
    ),
  },
  {
    id: 'effective_date',
    header: 'Effective Date',
    accessorKey: 'effective_date',
    meta: { sortField: 'effective_date' },
    size: 150,
    cell: ({ row }) =>
      row.original.effective_date ? formatDate(row.original.effective_date) : '-',
  },
  {
    id: 'total_amount',
    header: 'Total Amount',
    accessorKey: 'total_amount',
    meta: { sortField: 'total_amount' },
    size: 150,
    cell: ({ row }) => (
      <span className="font-medium">
        {formatCurrency(row.original.total_amount ?? 0, row.original.currency_iso_code || 'CAD')}
      </span>
    ),
  },
  {
    id: 'item_count',
    header: 'Items',
    accessorKey: 'item_count',
    size: 80,
    cell: ({ row }) => <span>{row.original.item_count ?? 0}</span>,
  },
  {
    id: 'owner_name',
    header: 'Owner',
    accessorKey: 'owner_name',
    meta: { sortField: 'owner_name' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">
        {(row.original as any).owner_name || '-'}
      </span>
    ),
  },
  {
    id: 'actions',
    header: () => <div className="text-right">Actions</div>,
    size: 100,
    minSize: 80,
    maxSize: 150,
    enableResizing: false,
    cell: ({ row }) => (
      <div className="text-right">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`View details for order ${row.original.order_number}`}
          asChild
        >
          <Link to={`/orders/${row.original.id}`}>View</Link>
        </Button>
      </div>
    ),
  },
]

export const ordersDefaultColumnSizing: ColumnSizingState = {
  order_number: 180,
  account_name: 200,
  status: 130,
  effective_date: 150,
  total_amount: 150,
  item_count: 80,
  owner_name: 150,
  actions: 100,
}
